import { Action } from "redux"
import env from "../../../../env"

import * as actions from "./actions"
import { ISmartLocation } from "./index"

export const initialState: ISmartLocation = {
    breadcrumbs: [],
    siblings: {
        siblingsContent: [],
        filter: undefined,
        lastSiblingId: undefined,
        size: undefined,
        isLoading: false,
        isLoadingMore: false,
        hasMore: false,
    },
    siblingsShown: false,
}

const ACTION_HANDLERS: {
    [action: string]: (state: ISmartLocation, props: Action) => ISmartLocation
} = {
    [actions.INITIALIZE]: (
        state,
        props: actions.Initialize,
    ): ISmartLocation => {
        if (!state.siblings) {
            return state
        }

        const { breadcrumbs } = props

        // we get the current location information from the url unless we have breadcrumbs
        // for example: workspace cover page has no breadcrumbs, but we still need to show the siblings
        //
        //  we base the current location on the first breadcrumb
        //  the first breadcrumb's location is not null
        //  which happens when the user doesn't have privs to see that location in the application
        //
        let currentLocation = window.location.pathname
        if (!!breadcrumbs && breadcrumbs.length > 0) {
            const newBreadcrumbs = breadcrumbs.map((crumb) => ({
                ...crumb,
                location: crumb.location === "null" ? null : location,
            }))

            if (!!newBreadcrumbs[0].location) {
                currentLocation = newBreadcrumbs[0].location
            }
        }

        // remove the application path, as we don't need to take that into account when we are analyzing the url
        // this also makes sure that if the application path has "dashboard" or "timesheet" in it
        // we won't fetch the wrong relative siblings
        currentLocation = currentLocation.replace(env.applicationPath, "")

        let location = ""
        if (currentLocation.includes("dashboard")) {
            location = "dashboard"
        } else if (currentLocation.includes("timesheet")) {
            location = "timesheet"
        } else if (currentLocation.split("workspace/").length > 1) {
            location = currentLocation.split("workspace/")[1].split("/")[0]
        } else if (currentLocation.includes("item/")) {
            location =
                breadcrumbs?.[1]?.location
                    ?.split("workspace/")?.[1]
                    ?.split("/")[0] ?? ""
        }

        return { ...state, siblings: { ...state.siblings, location }, ...props }
    },

    [actions.RECEIVE_SIBLINGS]: (
        state,
        { Data, Meta }: actions.ReceiveSiblings,
    ): ISmartLocation => {
        if (!Data || !Meta || !state.siblings) {
            return state
        }

        const newSiblingsArray =
            !!state.siblings.siblingsContent && state.siblings.isLoadingMore
                ? state.siblings.siblingsContent.concat(...Data)
                : Array.from(Data)

        return {
            ...state,
            siblings: {
                ...state.siblings,
                siblingsContent: newSiblingsArray,
                isLoading: false,
                hasMore: Meta.More,
            },
        }
    },

    [actions.UPDATE_SIBLINGS_VISIBILITY]: (
        state,
        { siblingsShown }: actions.UpdateSiblingsVisibility,
    ): ISmartLocation =>
        !!state.siblings
            ? !state.siblingsShown
                ? {
                      ...state,
                      siblingsShown,
                      siblings: { ...state.siblings, isLoadingMore: false },
                  }
                : {
                      ...state,
                      siblings: { ...state.siblings, isLoadingMore: false },
                  }
            : state,

    [actions.REORDER_SIBLINGS]: (
        state,
        {
            targetLocation,
            oldLocation,
            reorderedSiblingId,
            targetSiblingId,
        }: actions.ReorderSiblings,
    ): ISmartLocation => {
        if (
            !state.siblings ||
            oldLocation === targetLocation ||
            !state.siblings.siblingsContent
        ) {
            return state
        }

        const to = state.siblings.siblingsContent.findIndex(
            (sibling) => sibling.Id === targetSiblingId,
        )
        const from = state.siblings.siblingsContent.findIndex(
            (sibling) => sibling.Id === reorderedSiblingId,
        )

        const newSiblingsArray = state.siblings.siblingsContent.slice(0)
        newSiblingsArray.splice(to, 0, newSiblingsArray.splice(from, 1)[0])

        return {
            ...state,
            siblings: {
                ...state.siblings,
                siblingsContent: newSiblingsArray,
                lastSiblingId: undefined,
                size: state.siblings.siblingsContent.length,
            },
        }
    },

    [actions.REMOVE_SIBLING]: (
        state,
        { selectionFilterId }: actions.RemoveSibling,
    ): ISmartLocation => {
        if (
            !selectionFilterId ||
            !state.siblings ||
            !state.siblings.siblingsContent
        ) {
            return state
        }

        const newSiblingsArray = state.siblings.siblingsContent.filter(
            (sibling) => sibling.Id !== selectionFilterId,
        )

        return {
            ...state,
            siblings: {
                ...state.siblings,
                siblingsContent: newSiblingsArray,
                lastSiblingId: undefined,
                size: state.siblings.siblingsContent.length,
            },
        }
    },

    [actions.FILTER_SIBLINGS]: (
        state,
        { filter }: actions.FilterSibling,
    ): ISmartLocation =>
        !!state.siblings
            ? {
                  ...state,
                  siblings: {
                      ...state.siblings,
                      filter,
                      siblingsContent: [],
                      lastSiblingId: undefined,
                      size: undefined,
                      isLoading: true,
                  },
              }
            : state,

    [actions.LOAD_MORE_SIBLINGS]: (
        state,
        { lastSiblingId }: actions.LoadMoreSiblings,
    ): ISmartLocation =>
        !!state.siblings
            ? {
                  ...state,
                  siblings: {
                      ...state.siblings,
                      lastSiblingId,
                      size: undefined,
                      isLoadingMore: true,
                  },
              }
            : state,
}

export default function reducer(
    state: ISmartLocation = initialState,
    action: Action,
): ISmartLocation {
    const handler: (state: ISmartLocation, action: Action) => ISmartLocation =
        ACTION_HANDLERS[action.type]
    return handler ? handler(state, action) : state
}
